/**
 * @file ContractOrdersPanel.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The order lines counted against a contract, each marked
 *              Automatic (priced from it on the order screen, or Refresh
 *              auto-links) or Added by hand, with who and when — and, for
 *              someone who may edit contract pricing, "Add orders…" and
 *              "Take off" (Cam, 28 Oct 2026). Shown in the contract windows
 *              beside Linked Purchase Orders, and on its own from a contract
 *              row's "Add orders…" (ContractOrdersModal).
 *
 *              `set` is what the row stands for: { kind: 'contract', ids }
 *              (one contract, or a group's products), { kind: 'monthly', ids }
 *              or { kind: 'straights', id } (every month of a straights
 *              contract) or { kind: 'milk_powder', ids } (a milk powder
 *              customer + product's prices, 28 Oct 2026). The server decides
 *              everything (services/contractLinkService.js).
 *
 *              Usage counts TONNES (Cam, 28 Oct 2026): each line shows what it
 *              counts as, and a line counted as typed (Units None, or no unit
 *              weight anywhere) is flagged with how to fix it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { formatDateShort } from '../utils/dateFormatters';
import { setQuery, setKeyOf, sourceWords, usageWords } from '../utils/contractOrders';
import { formatTonnes } from '../utils/numberFormatters';
import { useEntityModal } from './EntityModalProvider';
import useAccess from '../hooks/useAccess';
import { useSlideOutButton } from './SlideOutButton';
import QtyWithTonnes from './QtyWithTonnes';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import AddContractOrdersModal from './AddContractOrdersModal';
import './LinkedPurchaseOrdersPanel.css';
import './ContractOrdersPanel.css';

function ContractOrdersPanel({
  set,
  // Whether this view may change links: the caller's say (a window in view
  // mode passes false), else the person's 'Edit contract pricing'.
  canEdit: canEditProp,
  editBlock = null,
  // Who may change links here (a milk powder price: either edit permission).
  editPermissions = ['contract_pricing.edit'],
  title = 'Orders counted against this contract',
  startAdding = false,
  onChanged = null
}) {
  const { openEntity } = useEntityModal();
  const access = useAccess();
  const mayEdit = editPermissions.some(k => access.canWrite(k));
  const canEdit = canEditProp === undefined ? mayEdit : (canEditProp && mayEdit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ ready: true, reason: null, targets: [], lines: [] });
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null);
  const key = setKeyOf(set);

  const load = useCallback(async () => {
    if (!set) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/pricing/contract-orders?${setQuery(set)}`, { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (startAdding) setAdding(true); }, [startAdding]);

  const block = !canEdit
    ? (mayEdit ? (editBlock || 'Edit the contract to add or take off orders')
      : (editPermissions.includes('milk_powder.edit') ? "Needs 'Edit milk powder pricing'" : "Needs 'Edit contract pricing'"))
    : (!data.ready ? data.reason : null);
  const multiTarget = (data.targets || []).length > 1;
  const manualCount = data.lines.filter(l => l.source === 'manual').length;

  useSlideOutButton([loading, data.lines.length, !!block]);

  const changed = () => { load(); onChanged?.(); };

  const takeOff = async () => {
    const line = removing;
    setRemoving(null);
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/contract-orders/unlink`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_line_ids: [line.order_line_id], set })
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      changed();
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="linked-pos-panel contract-orders-panel">
      <div className="linked-pos-panel-header">
        <h4>{title}</h4>
        <span className="linked-pos-summary">
          {loading ? 'Loading…' : `${usageWords(data.usage, formatTonnes)}${manualCount ? ` · ${manualCount} added by hand` : ''}`}
        </span>
        <button
          type="button"
          className={`btn-slideout${block ? ' btn-disabled' : ''}`}
          onClick={block ? undefined : () => setAdding(true)}
          disabled={!!block || busy}
          title={block || 'Count more order lines against this contract — any product, this customer\'s orders'}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
          <span className="btn-label">Add orders…</span>
        </button>
      </div>

      {error && <div className="linked-pos-error">Couldn't load the orders: {error}</div>}
      {!loading && !error && data.lines.length === 0 && (
        <div className="linked-pos-empty">No order lines count against this contract yet.</div>
      )}

      {data.lines.length > 0 && (
        <div className="linked-pos-table-wrap">
          <table className="linked-pos-table contract-orders-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Delivery</th>
                <th>Product</th>
                <th className="text-right">Qty</th>
                {multiTarget && <th>Counts against</th>}
                <th>How</th>
                <th className="actions-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map(l => {
                const removeBlock = block
                  || (l.archived ? 'The order is archived' : null);
                return (
                  <tr key={l.order_line_id} title="Open the order" onClick={() => openEntity('customer_order', l.order_id, { onClose: load })}>
                    <td className="nowrap">
                      <strong>{l.order_number}</strong>
                      {l.split_from && <span className="contract-orders-note">Split from {l.split_from}</span>}
                    </td>
                    <td>
                      {l.account_no && <span className="po-code-badge">{l.account_no}</span>} {l.customer}
                    </td>
                    <td className="nowrap">{formatDateShort(l.delivery_date)}</td>
                    <td className="po-desc">
                      {l.item_code && <span className="po-code-badge">{l.item_code}</span>} {l.product_name}
                    </td>
                    <td className="text-right">
                      <QtyWithTonnes quantity={l.quantity} line={l} />
                      {l.counts_flag && <span className="contract-orders-note is-warning" title={l.counts_flag}>Counts as typed</span>}
                    </td>
                    {multiTarget && <td className="po-desc">{l.link?.label || '—'}</td>}
                    <td>
                      <span className={`source-badge ${l.source === 'manual' ? 'source-manual' : 'source-auto'}`}
                        title={sourceWords(l, formatDateShort)}>
                        {l.source === 'manual' ? 'BY HAND' : 'AUTO'}
                      </span>
                      {l.invoiced && <span className="contract-orders-note">Invoiced</span>}
                      {l.link && l.link.may_count === false && (
                        <span className="contract-orders-note is-warning">Not this customer's contract — on the review list</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="actions-cell">
                        <button
                          type="button"
                          className={`btn-icon${removeBlock ? ' btn-disabled' : ''}`}
                          title={removeBlock || 'Take this order line off the contract (it is not linked back automatically)'}
                          disabled={!!removeBlock || busy}
                          onClick={removeBlock ? undefined : () => setRemoving(l)}
                          data-write-action
                        >
                          <span className="material-symbols-rounded">link_off</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddContractOrdersModal
          set={set}
          targets={data.targets}
          onClose={() => setAdding(false)}
          onAdded={() => { setAdding(false); changed(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!removing}
        type="warning"
        title="Take off the contract?"
        message={removing
          ? `${removing.order_number} (${removing.item_code || removing.product_name}) will no longer count against ${removing.link?.label || 'this contract'}. Refresh auto-links won't put it back; you can add it again by hand.`
          : ''}
        confirmText="Take off"
        confirmIcon="link_off"
        onConfirm={takeOff}
        onCancel={() => setRemoving(null)}
      />
      <AlertDialog
        isOpen={!!alert}
        type={alert?.type || 'info'}
        message={alert?.message}
        onClose={() => setAlert(null)}
      />
    </div>
  );
}

export default ContractOrdersPanel;
